/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { SAME_POINT_DISTANCE_KM } from './constants';
import { SOUNDING_COLORS } from './drawUtils';
import type { SourceType } from '../../store/types';
import {
  bearingDeg,
  bearingToCompass8,
  formatDistanceKm,
  formatLatLon,
  getDisplayValue,
  haversineDistanceKm,
} from '../utils';

export const getLegendTitle = (source: SourceType): string => {
  const displayName = source.name || source.collectionId;
  const displayInstance = getDisplayValue(
    source.instanceId || '',
    "yyyy-MM-dd HH:mm'Z'",
  );
  return `${displayName} ${displayInstance}`;
};

export const getLegendTooltipLines = (
  source: SourceType,
  gridPoint: [number, number] | undefined,
  selectedPoint: [number, number] | undefined,
  locationName?: string,
): string[] => {
  const lines = [getLegendTitle(source)];
  if (!gridPoint) {
    return lines;
  }
  const coords = formatLatLon(gridPoint);
  lines.push(locationName ? `${locationName} (${coords})` : coords);
  if (!selectedPoint) {
    return lines;
  }
  const distanceKm = haversineDistanceKm(selectedPoint, gridPoint);
  if (distanceKm < SAME_POINT_DISTANCE_KM) {
    lines.push('at selected point');
  } else {
    const compass = bearingToCompass8(bearingDeg(selectedPoint, gridPoint));
    lines.push(`${formatDistanceKm(distanceKm)} ${compass} of selected point`);
  }
  return lines;
};

interface SoundingsLegendProps {
  sourceSelections: SourceType[];
  sourceGridPoints?: Record<string, [number, number] | undefined>;
  sourceLocationNames?: Record<string, string | undefined>;
  selectedPoint?: [number, number];
}

export const SoundingsLegend = ({
  sourceSelections,
  sourceGridPoints,
  sourceLocationNames,
  selectedPoint,
}: SoundingsLegendProps): React.ReactElement => {
  return (
    <Stack
      direction="row"
      justifyContent="center"
      spacing={1}
      sx={{ flexWrap: 'wrap', paddingLeft: 2, paddingTop: 0.5 }}
      useFlexGap
    >
      {sourceSelections.map((source: SourceType, index) => {
        const title = getLegendTitle(source);
        const lines = getLegendTooltipLines(
          source,
          sourceGridPoints?.[source.collectionId],
          selectedPoint,
          sourceLocationNames?.[source.collectionId],
        );
        const tooltipNode = (
          <Box>
            {lines.map((line) => (
              <Box key={line}>{line}</Box>
            ))}
          </Box>
        );
        return (
          <Tooltip
            key={`${source.collectionId}-${source.instanceId}`}
            title={tooltipNode}
          >
            <Typography
              sx={{
                color: 'geowebColors.typographyAndIcons.text',
                fontSize: '10px',
              }}
              variant="caption"
            >
              <Box
                sx={{
                  width: '16px',
                  height: '8px',
                  backgroundColor: SOUNDING_COLORS[index],
                  display: 'inline-block',
                  marginRight: '4px',
                }}
              />
              {title}
            </Typography>
          </Tooltip>
        );
      })}
    </Stack>
  );
};
